import React, { useState } from 'react'
import { Box, Button, Skeleton, Tooltip, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import ShuffleRoundedIcon from '@mui/icons-material/ShuffleRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import { useMediaState } from '../../context/media-state-context'
import { useAuth } from '../../context/AuthContext'
import { parseUtcDate } from '../../utils/parse-utc-date'
import AvatarUploadModal from '../../components/avatar-upload-modal'
import LazyImage from '../../components/lazy-image'
import { AvatarWrapper, StyledAvatar } from './profile-styles'
import { RailCard } from './rail-styles'

const EditAvatarButton = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  right: 0,
  width: 28,
  height: 28,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.brand.main,
    borderColor: theme.palette.brand.main,
  },
}))

const DisplayName = styled(Typography)(({ theme }) => ({
  fontSize: 22,
  fontWeight: 700,
  lineHeight: 1.2,
  marginTop: theme.spacing(2),
  color: theme.palette.text.primary,
  textAlign: 'center',
}))

const UsernameLink = styled(RouterLink)(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  display: 'block',
  textAlign: 'center',
  '&:hover': {
    textDecoration: 'underline',
    color: theme.palette.brand.main,
  },
}))

// Bio is free text and can run long; three lines keeps the rail predictable
// until the user expands it.
const Bio = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'clamped',
})<{ clamped: boolean }>(({ theme, clamped }) => ({
  fontSize: 13,
  lineHeight: 1.55,
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(1.5),
  ...(clamped && {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  }),
}))

const BioToggle = styled('button')(({ theme }) => ({
  display: 'block',
  marginTop: theme.spacing(0.5),
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: 'inherit',
  color: theme.palette.brand.main,
  '&:hover': { textDecoration: 'underline' },
}))

const Divider = styled(Box)(({ theme }) => ({
  height: 1,
  backgroundColor: theme.palette.divider,
  marginTop: theme.spacing(2.5),
  marginBottom: theme.spacing(2),
}))

const StatRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  paddingTop: theme.spacing(0.75),
  paddingBottom: theme.spacing(0.75),
}))

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.secondary,
}))

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: 700,
  color: theme.palette.text.primary,
}))

const SurpriseMeButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: theme.palette.brand.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.brand.secondary,
  },
}))

const JoinedCaption = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontStyle: 'italic',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(1.5),
  textAlign: 'center',
}))

// Bios shorter than this render in full - only a genuinely long one is worth
// collapsing behind a toggle.
const BIO_CLAMP_THRESHOLD = 155

const stats = [
  { label: 'Episodes watched', key: 'totalEpisodesWatched' },
  { label: 'Movies watched', key: 'totalMoviesWatched' },
  { label: 'Shows tracked', key: 'totalShowsStarted' },
] as const

export const IdentityCard: React.FC = () => {
  const { mediaState, isFetching } = useMediaState()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [avatarModalOpen, setAvatarModalOpen] = useState(false)
  const [bioExpanded, setBioExpanded] = useState(false)

  // Only a genuinely long bio gets clamped; anything shorter renders in full
  // with no toggle at all.
  const isLongBio = (user?.bio?.length ?? 0) >= BIO_CLAMP_THRESHOLD

  // Watchlist entries that are already out (or whose year we can't tell) -
  // landing on a title you can't watch yet defeats the point.
  const surprisePool = (mediaState?.watchlist ?? []).filter(
    (item) => !item.releaseYear || item.releaseYear <= new Date().getFullYear(),
  )

  const surpriseMe = () => {
    if (surprisePool.length === 0) return
    const pick = surprisePool[Math.floor(Math.random() * surprisePool.length)]
    const type = pick.mediaType.toLowerCase() === 'movie' ? 'movie' : 'show'
    navigate(`/details/${type}/${pick.tmdbId}`)
  }

  return (
    <RailCard>
      <AvatarWrapper
        sx={{ display: 'block', width: 'fit-content', mx: 'auto' }}
      >
        <StyledAvatar>
          <LazyImage
            src={user?.profilePictureUrl}
            alt='Profile'
            fallback={user?.name?.charAt(0)}
            variant='circular'
          />
        </StyledAvatar>
        <Tooltip title='Change avatar'>
          <EditAvatarButton
            role='button'
            tabIndex={0}
            aria-label='Change avatar'
            onClick={() => setAvatarModalOpen(true)}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                setAvatarModalOpen(true)
              }
            }}
          >
            <EditRoundedIcon sx={{ fontSize: 15 }} />
          </EditAvatarButton>
        </Tooltip>
      </AvatarWrapper>

      <DisplayName>{user?.name}</DisplayName>
      {user?.username && (
        <UsernameLink to={`/user/${user.username}`}>
          @{user.username}
        </UsernameLink>
      )}
      {user?.bio && (
        <>
          <Bio clamped={isLongBio && !bioExpanded}>{user.bio}</Bio>
          {isLongBio && (
            <BioToggle onClick={() => setBioExpanded((previous) => !previous)}>
              {bioExpanded ? 'Show less' : 'Show more'}
            </BioToggle>
          )}
        </>
      )}

      <Divider />

      {stats.map((stat) => (
        <StatRow key={stat.key}>
          <StatLabel>{stat.label}</StatLabel>
          {isFetching && !mediaState ? (
            <Skeleton variant='text' width={48} height={26} />
          ) : (
            <StatValue>
              {(mediaState?.stats?.[stat.key] ?? 0).toLocaleString()}
            </StatValue>
          )}
        </StatRow>
      ))}

      <Tooltip
        title={
          surprisePool.length === 0
            ? 'Add something to your watchlist first'
            : 'Open a random title from your watchlist'
        }
      >
        <span>
          <SurpriseMeButton
            fullWidth
            variant='contained'
            disableElevation
            startIcon={<ShuffleRoundedIcon />}
            onClick={surpriseMe}
            disabled={surprisePool.length === 0}
          >
            Surprise me
          </SurpriseMeButton>
        </span>
      </Tooltip>

      {user?.createdOnUtc && (
        <JoinedCaption>
          Movieceptionite since {parseUtcDate(user.createdOnUtc).getFullYear()}
        </JoinedCaption>
      )}

      <AvatarUploadModal
        open={avatarModalOpen}
        onClose={() => setAvatarModalOpen(false)}
      />
    </RailCard>
  )
}

export default IdentityCard
